import * as Sentry from '@sentry/react'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import dayjs from 'dayjs'

import { HttpService } from '@/api/shared/http.service'
import { tryParseJson } from '@/app/utils'
import {
  SELECTED_PROJECT_COOKIE_KEY,
  useSelectedProjectStore
} from '@/components/entities/common/model/selected-project.store'

import { UserInfoMapper } from './auth.mapper'
import {
  GrantTypeEnum,
  SignByCodeInArgs,
  SignInResponse,
  UserInfoResponse
} from './auth.types'
import { isSelectedProjectAvailable } from './auth.utils'

export const BEARER = 'Bearer '

class AuthService extends HttpService {
  TOKENS_COOKIES_KEY = 'x-auth'
  CLIENT_ID = 'front-client'

  saveTokens({
    access_token,
    refresh_token,
    refresh_expires_in
  }: SignInResponse) {
    setCookie(
      this.TOKENS_COOKIES_KEY,
      {
        accessToken: access_token,
        refreshToken: refresh_token
      },
      {
        expires: dayjs().add(refresh_expires_in, 'seconds').toDate(),
        sameSite: 'strict',
        secure: true
      }
    )
  }

  clearSavedTokens() {
    this.setAuthHeader()
    deleteCookie(this.TOKENS_COOKIES_KEY)
  }

  getTokens(): { accessToken?: string; refreshToken?: string } {
    const result = tryParseJson(getCookie(this.TOKENS_COOKIES_KEY))
    return result || {}
  }

  applySavedToken(tokens?: { accessToken?: string; refreshToken?: string }) {
    const { accessToken } = tokens ?? this.getTokens()
    if (accessToken) {
      this.setAuthHeader(accessToken)
      return true
    }
    return false
  }

  setAuthHeader(accessToken?: string) {
    this.axios.defaults.headers.common['Authorization'] = accessToken
      ? BEARER + accessToken
      : ''
  }

  processTokens(data: SignInResponse) {
    this.saveTokens(data)
    this.setAuthHeader(data.access_token)
  }

  async refreshToken() {
    const { refreshToken } = this.getTokens()
    if (!refreshToken) {
      this.logout()
      return
    }

    const { data } = await this.axios.post<SignInResponse>(
      this.getPath('/token'),
      {
        client_id: this.CLIENT_ID,
        grant_type: GrantTypeEnum.REFRESH_TOKEN,
        refresh_token: refreshToken
      },
      {
        headers: { 'content-type': 'application/x-www-form-urlencoded' }
      }
    )

    this.processTokens(data)
    return data
  }

  async sendSmsCode(data: { phone_number?: string; email?: string }) {
    return this.makeSafeRequest<{ code?: string }>({
      method: 'POST',
      url: this.getPath('/send'),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      data
    })
  }

  async getCurrentUser() {
    const { refreshToken } = this.getTokens()
    if (!refreshToken) {
      return
    }
    const { data } = await this.axios.get<UserInfoResponse>(
      this.getPath('/userInfo')
    )
    if (data.projects.length === 0) {
      deleteCookie(SELECTED_PROJECT_COOKIE_KEY)
      useSelectedProjectStore.getState().setSelectedProjectId('')
      return UserInfoMapper.toEntity(data)
    }

    if (!isSelectedProjectAvailable(data.projects)) {
      setCookie(SELECTED_PROJECT_COOKIE_KEY, data.projects[0].id)
      useSelectedProjectStore
        .getState()
        .setSelectedProjectId(data.projects[0].id)
    }

    return UserInfoMapper.toEntity(data)
  }

  async signInByCode({ email, phone_number, smsCode }: SignByCodeInArgs) {
    const { ok, data, errors } = await this.makeSafeRequest<SignInResponse>({
      method: 'POST',
      url: this.getPath('/token'),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      data: {
        client_id: this.CLIENT_ID,
        grant_type: GrantTypeEnum.PASSWORD,
        code: smsCode,
        ...(email && { email }),
        ...(phone_number && { phone_number })
      }
    })

    if (ok && data) {
      this.processTokens(data)
    }
    return { ok, data, errors }
  }

  logout(path = '/auth/signin') {
    deleteCookie(SELECTED_PROJECT_COOKIE_KEY)
    this.clearSavedTokens()
    Sentry.setUser(null)

    if (location.pathname !== path) {
      location.assign(path)
    }
  }
}

const AuthServiceSingleton = new AuthService('/api/v1/auth/')
export { AuthServiceSingleton as AuthService }
